import { Container, Reveal, SectionHead, PageHero } from "../components/site/Primitives";
import { IMG } from "../data/site";
import { Sun, Gauge, Feather, Recycle, Package, RefreshCcw, Zap, TrendingDown, LineChart } from "lucide-react";

const Card = ({ icon: Icon, title, body, i, testId }) => (
  <Reveal delay={i * 0.08}>
    <div data-testid={testId} className="lift h-full bg-white border border-slate-200 p-8 lg:p-10">
      <Icon className="h-7 w-7 text-brand" strokeWidth={1.6} />
      <h3 className="font-display mt-6 text-xl font-semibold text-ink">{title}</h3>
      <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{body}</p>
    </div>
  </Reveal>
);

const CIRCULAR = [
  [Recycle, "Material selection", "Closures are produced from grades suitable for food-contact use and widely accepted in recycling streams for HDPE and PP where applicable."],
  [RefreshCcw, "Recycled content (PCR)", "Where specifications allow, we evaluate post-consumer recycled content in consultation with brand owners and regulatory requirements."],
  [Package, "End of life", "Designs favour mono-material construction where possible to simplify sorting and improve recyclability outcomes."],
];
const OPS = [
  [Zap, "SACMI compression", "Compression molding can offer lower specific energy per part compared with conventional injection for many closure formats, supporting your carbon reporting."],
  [TrendingDown, "Lower process losses", "Stable cycles and in-line checks reduce scrap and the need for re-grind handling on site."],
  [LineChart, "Continuous improvement", "We track KPIs on efficiency and quality so that environmental and commercial performance move together."],
];

const Solar = () => (
  <section className="py-24 lg:py-32" data-testid="sustainability-solar">
    <Container className="grid gap-14 lg:grid-cols-2 lg:gap-20 items-center">
      <Reveal><div className="img-zoom overflow-hidden aspect-[4/3]"><img src={IMG("about_policy-green-solar")} alt="Solar renewable energy at MU Plast" className="h-full w-full object-cover" /></div></Reveal>
      <div>
        <SectionHead eyebrow="Green energy" title="Powered by the sun, built for efficiency" />
        <Reveal delay={0.1} className="mt-6 space-y-6">
          <p className="text-slate-600 leading-relaxed">By utilizing solar power, we actively lower our carbon footprint and contribute towards environmentally responsible manufacturing. By integrating solar energy, improving energy efficiency, and optimizing production processes, we lower emissions while maintaining high product performance and quality.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[[Sun, "Solar power", "Renewable energy integrated into plant operations."], [Gauge, "Energy & utilities", "Efficient monitoring and responsible use of electricity and compressed air."]].map(([I, t, b]) => (
              <div key={t} className="border-l-2 border-brand pl-5"><I className="h-5 w-5 text-brand" /><p className="font-display mt-3 font-semibold text-ink">{t}</p><p className="mt-1 text-sm text-slate-600">{b}</p></div>
            ))}
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

const Lightweight = () => (
  <section className="bg-mist py-24 lg:py-32" data-testid="sustainability-lightweighting">
    <Container>
      <SectionHead eyebrow="Lightweighting initiative" title="Less polymer per closure, same seal performance" lead="Reducing polymer per closure lowers raw-material use and transport impact while maintaining seal performance and line compatibility." />
      <div className="mt-14 grid gap-6 sm:grid-cols-3">
        {[["2.5g", "Reference design", "per closure (example)"], ["1.8g", "Lightweight design", "per closure (example)"], ["0.7g", "Material reduction", "reduction per closure"]].map(([v, t, s], i) => (
          <Reveal key={t} delay={i * 0.08} className="lift bg-white border border-slate-200 p-8 lg:p-10">
            <Feather className={`h-6 w-6 ${i === 2 ? "text-brand" : "text-slate-300"}`} />
            <p className={`font-display mt-6 text-5xl lg:text-6xl font-semibold tracking-tight ${i === 2 ? "text-brand" : "text-navy"}`}>{v}</p>
            <p className="mt-3 font-medium text-ink">{t}</p>
            <p className="text-sm text-slate-500">{s}</p>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-sm text-slate-500">Figures are illustrative; your account manager can provide estimates based on your volumes and SKU mix.</p>
    </Container>
  </section>
);

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <PageHero testId="sustainability-hero" eyebrow="Responsibility" title="Sustainability" img={IMG("about_policy-green-solar")} lead="Lightweight designs, material efficiency, and responsible operations are at the core of how we manufacture." />
      <Solar />
      <Lightweight />
      <section className="py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Circular economy" title="Designed with recycling in mind" lead="We design closures from materials that support recycling where collection and reprocessing infrastructure exist, and we work with customers on specifications that align with regional rules." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">{CIRCULAR.map(([I, t, b], i) => <Card key={t} icon={I} title={t} body={b} i={i} testId={`circular-card-${i}`} />)}</div>
        </Container>
      </section>
      <section className="bg-mist py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Operational efficiency" title="Waste handling & efficient operations" lead="Energy use and process stability are part of our sustainability footprint: efficient equipment and trained teams reduce waste and rework. We promote responsible waste management through scrap segregation and approved recycling practices." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">{OPS.map(([I, t, b], i) => <Card key={t} icon={I} title={t} body={b} i={i} testId={`ops-card-${i}`} />)}</div>
        </Container>
      </section>
    </div>
  );
}
